import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

// . Threshold structure
interface Threshold {
  id?: string;
  from: string;
  to: string;
  approvers: string;
  fromAmount?: number;
  toAmount?: number;
  requiredApprovers?: number;
  paymentTypeId?: number;
}

// . Account details structure
interface AccountDetails {
  accountId: number;
  accountName?: string;
  accountNickname?: string;
  accountNo?: string;
  accountIbanNo?: string;
  accountType?: string;
  accountCurrencyCode?: string;
  accountBankName?: string;
  accountAvailBalance?: number;
  accountStatus?: string;
  status?: string;
  paymentTypeId?: number;
  subFeatureId?: number;
  countryCode2?: string;
  currencyCode2?: string;
  cardNo?: string;
  [key: string]: any;
}

// . Account auth matrix structure
interface AccountAuthMatrix {
  accountDetails: AccountDetails;
  apiThresholds: Threshold[];
  pendingThresholds: Threshold[];
  mergedThresholds: Threshold[];
}

// . Payment type structure
interface PaymentTypeData {
  accounts: {
    [accountId: number]: AccountAuthMatrix;
  };
  generalThresholds: {
    apiThresholds: Threshold[];
    pendingThresholds: Threshold[];
    mergedThresholds: Threshold[];
  };
}

// . State structure
interface AuthMatrixState {
  byPaymentType: {
    [paymentTypeId: number]: PaymentTypeData;
  };
  selectedAccounts: {
    [paymentTypeId: number]: number[]; // Array of selected account IDs per payment type
  };
}

const initialState: AuthMatrixState = {
  byPaymentType: {},
  selectedAccounts: {},
};

const authMatrixSlice = createSlice({
  name: "authMatrix",
  initialState,
  reducers: {
    // . Set API data for a specific account
    setApiThresholds: (
      state,
      action: PayloadAction<{
        paymentTypeId: number;
        accountId: number;
        accountDetails: AccountDetails;
        thresholds: Threshold[];
      }>
    ) => {
      const { paymentTypeId, accountId, accountDetails, thresholds } =
        action.payload;

      console.log(
        "🔵 [GX_REDUX] Setting API thresholds for:",
        `PaymentType: ${paymentTypeId}, Account: ${accountId}`
      );
      console.log("📥 [GX_REDUX] API Data:", thresholds);

      // Initialize payment type if not exists
      if (!state.byPaymentType[paymentTypeId]) {
        state.byPaymentType[paymentTypeId] = {
          accounts: {},
          generalThresholds: {
            apiThresholds: [],
            pendingThresholds: [],
            mergedThresholds: [],
          },
        };
      }

      // Initialize account if not exists
      if (!state.byPaymentType[paymentTypeId].accounts[accountId]) {
        state.byPaymentType[paymentTypeId].accounts[accountId] = {
          accountDetails,
          apiThresholds: [],
          pendingThresholds: [],
          mergedThresholds: [],
        };
      }

      // Set API thresholds
      state.byPaymentType[paymentTypeId].accounts[accountId].apiThresholds =
        thresholds;

      // Merge with pending thresholds
      const pending =
        state.byPaymentType[paymentTypeId].accounts[accountId]
          .pendingThresholds;
      state.byPaymentType[paymentTypeId].accounts[accountId].mergedThresholds =
        [...thresholds, ...pending];

      console.log(
        ". [GX_REDUX] API Thresholds stored:",
        state.byPaymentType[paymentTypeId].accounts[accountId].apiThresholds
      );
      console.log(
        "🔀 [GX_REDUX] Merged Thresholds (API + Pending):",
        state.byPaymentType[paymentTypeId].accounts[accountId].mergedThresholds
      );
    },

    // . Add new threshold to pending (not saved yet)
    addPendingThreshold: (
      state,
      action: PayloadAction<{
        paymentTypeId: number;
        accountId: number;
        threshold: Threshold;
      }>
    ) => {
      const { paymentTypeId, accountId, threshold } = action.payload;

      console.log(
        "🟢 [GX_REDUX] Adding pending threshold for:",
        `PaymentType: ${paymentTypeId}, Account: ${accountId}`
      );
      console.log("➕ [GX_REDUX] New Threshold:", threshold);

      // Initialize if not exists
      if (!state.byPaymentType[paymentTypeId]) {
        state.byPaymentType[paymentTypeId] = {
          accounts: {},
          generalThresholds: {
            apiThresholds: [],
            pendingThresholds: [],
            mergedThresholds: [],
          },
        };
      }

      if (!state.byPaymentType[paymentTypeId].accounts[accountId]) {
        state.byPaymentType[paymentTypeId].accounts[accountId] = {
          accountDetails: { accountId } as AccountDetails,
          apiThresholds: [],
          pendingThresholds: [],
          mergedThresholds: [],
        };
      }

      // Add to pending
      state.byPaymentType[paymentTypeId].accounts[
        accountId
      ].pendingThresholds.push(threshold);

      // Update merged thresholds
      const apiThresholds =
        state.byPaymentType[paymentTypeId].accounts[accountId].apiThresholds;
      const pendingThresholds =
        state.byPaymentType[paymentTypeId].accounts[accountId]
          .pendingThresholds;

      state.byPaymentType[paymentTypeId].accounts[accountId].mergedThresholds =
        [...apiThresholds, ...pendingThresholds];

      console.log(
        ". [GX_REDUX] Pending Thresholds:",
        state.byPaymentType[paymentTypeId].accounts[accountId]
          .pendingThresholds
      );
      console.log(
        "🔀 [GX_REDUX] Merged Thresholds (API + Pending):",
        state.byPaymentType[paymentTypeId].accounts[accountId].mergedThresholds
      );
    },

    // . NEW: Edit existing threshold (both API and pending)
    editThreshold: (
      state,
      action: PayloadAction<{
        paymentTypeId: number;
        accountId: number;
        thresholdId: string;
        updatedThreshold: Threshold;
      }>
    ) => {
      const { paymentTypeId, accountId, thresholdId, updatedThreshold } =
        action.payload;

      console.log(
        "✏️ [GX_REDUX] Editing threshold:",
        `PaymentType: ${paymentTypeId}, Account: ${accountId}, ID: ${thresholdId}`
      );
      console.log("📝 [GX_REDUX] Updated data:", updatedThreshold);

      if (!state.byPaymentType[paymentTypeId]?.accounts[accountId]) {
        console.error("❌ [GX_REDUX] Account not found in state");
        return;
      }

      const accountData = state.byPaymentType[paymentTypeId].accounts[accountId];

      // . Check if it's an API threshold
      const apiIndex = accountData.apiThresholds.findIndex(
        (t) => t.id === thresholdId
      );

      if (apiIndex !== -1) {
        console.log("🔵 [GX_REDUX] Editing API threshold at index:", apiIndex);
        accountData.apiThresholds[apiIndex] = {
          ...accountData.apiThresholds[apiIndex],
          ...updatedThreshold,
          id: thresholdId, // Preserve original ID
        };
      } else {
        // . Check if it's a pending threshold
        const pendingIndex = accountData.pendingThresholds.findIndex(
          (t) => t.id === thresholdId
        );

        if (pendingIndex !== -1) {
          console.log(
            "🟢 [GX_REDUX] Editing pending threshold at index:",
            pendingIndex
          );
          accountData.pendingThresholds[pendingIndex] = {
            ...accountData.pendingThresholds[pendingIndex],
            ...updatedThreshold,
            id: thresholdId, // Preserve original ID
          };
        } else {
          console.error("❌ [GX_REDUX] Threshold not found:", thresholdId);
          return;
        }
      }

      // . Regenerate merged thresholds
      accountData.mergedThresholds = [
        ...accountData.apiThresholds,
        ...accountData.pendingThresholds,
      ];

      console.log(". [GX_REDUX] Threshold edited successfully");
      console.log(
        "🔀 [GX_REDUX] Updated merged thresholds:",
        accountData.mergedThresholds
      );
    },

    // . Update account details
    updateAccountDetails: (
      state,
      action: PayloadAction<{
        paymentTypeId: number;
        accountId: number;
        accountDetails: AccountDetails;
      }>
    ) => {
      const { paymentTypeId, accountId, accountDetails } = action.payload;

      console.log(
        "🔄 [GX_REDUX] Updating account details for:",
        `PaymentType: ${paymentTypeId}, Account: ${accountId}`
      );

      if (state.byPaymentType[paymentTypeId]?.accounts[accountId]) {
        state.byPaymentType[paymentTypeId].accounts[
          accountId
        ].accountDetails = accountDetails;

        console.log(". [GX_REDUX] Account details updated:", accountDetails);
      }
    },

    // . Set selected accounts for a payment type
    setSelectedAccounts: (
      state,
      action: PayloadAction<{
        paymentTypeId: number;
        accountIds: number[];
      }>
    ) => {
      const { paymentTypeId, accountIds } = action.payload;

      console.log(
        "📋 [GX_REDUX] Setting selected accounts for PaymentType:",
        paymentTypeId
      );
      console.log(". [GX_REDUX] Selected account IDs:", accountIds);

      state.selectedAccounts[paymentTypeId] = accountIds;

      console.log(". [GX_REDUX] Selected accounts stored");
    },

    // . Clear selected accounts for a payment type
    clearSelectedAccounts: (
      state,
      action: PayloadAction<{
        paymentTypeId: number;
      }>
    ) => {
      const { paymentTypeId } = action.payload;

      console.log(
        "🗑️ [GX_REDUX] Clearing selected accounts for PaymentType:",
        paymentTypeId
      );

      delete state.selectedAccounts[paymentTypeId];

      console.log(". [GX_REDUX] Selected accounts cleared");
    },

    // . Clear pending thresholds after successful save
    clearPendingThresholds: (
      state,
      action: PayloadAction<{
        paymentTypeId: number;
        accountId?: number;
      }>
    ) => {
      const { paymentTypeId, accountId } = action.payload;

      console.log(
        "🗑️ [GX_REDUX] Clearing pending thresholds for:",
        `PaymentType: ${paymentTypeId}${
          accountId ? `, Account: ${accountId}` : " (All accounts)"
        }`
      );

      if (accountId) {
        // Clear specific account
        if (state.byPaymentType[paymentTypeId]?.accounts[accountId]) {
          state.byPaymentType[paymentTypeId].accounts[
            accountId
          ].pendingThresholds = [];

          // Update merged to only have API data
          state.byPaymentType[paymentTypeId].accounts[
            accountId
          ].mergedThresholds =
            state.byPaymentType[paymentTypeId].accounts[accountId].apiThresholds;

          console.log(". [GX_REDUX] Pending thresholds cleared for account");
        }
      } else {
        // Clear all accounts for this payment type
        if (state.byPaymentType[paymentTypeId]) {
          Object.keys(state.byPaymentType[paymentTypeId].accounts).forEach(
            (accId) => {
              const accountIdNum = parseInt(accId);
              state.byPaymentType[paymentTypeId].accounts[
                accountIdNum
              ].pendingThresholds = [];

              state.byPaymentType[paymentTypeId].accounts[
                accountIdNum
              ].mergedThresholds =
                state.byPaymentType[paymentTypeId].accounts[
                  accountIdNum
                ].apiThresholds;
            }
          );

          console.log(
            ". [GX_REDUX] Pending thresholds cleared for all accounts"
          );
        }
      }
    },

    // . Clear all data for a payment type (when switching transaction types)
    clearPaymentTypeData: (
      state,
      action: PayloadAction<{ paymentTypeId: number }>
    ) => {
      const { paymentTypeId } = action.payload;

      console.log(
        "🗑️ [GX_REDUX] Clearing all data for PaymentType:",
        paymentTypeId
      );

      delete state.byPaymentType[paymentTypeId];
      delete state.selectedAccounts[paymentTypeId];

      console.log(". [GX_REDUX] Payment type data cleared");
    },

    // . Reset entire state
    resetAuthMatrix: (state) => {
      console.log("🔄 [GX_REDUX] Resetting entire auth matrix state");
      state.byPaymentType = {};
      state.selectedAccounts = {};
      console.log(". [GX_REDUX] State reset complete");
    },

    // . Batch set API data for multiple accounts
    batchSetApiThresholds: (
      state,
      action: PayloadAction<{
        paymentTypeId: number;
        accountsData: Array<{
          accountId: number;
          accountDetails: AccountDetails;
          thresholds: Threshold[];
        }>;
      }>
    ) => {
      const { paymentTypeId, accountsData } = action.payload;

      console.log(
        ". [GX_REDUX] Batch setting API thresholds for PaymentType:",
        paymentTypeId
      );
      console.log("📊 [GX_REDUX] Number of accounts:", accountsData.length);

      // Initialize payment type if not exists
      if (!state.byPaymentType[paymentTypeId]) {
        state.byPaymentType[paymentTypeId] = {
          accounts: {},
          generalThresholds: {
            apiThresholds: [],
            pendingThresholds: [],
            mergedThresholds: [],
          },
        };
      }

      accountsData.forEach(({ accountId, accountDetails, thresholds }) => {
        // Initialize account if not exists
        if (!state.byPaymentType[paymentTypeId].accounts[accountId]) {
          state.byPaymentType[paymentTypeId].accounts[accountId] = {
            accountDetails,
            apiThresholds: [],
            pendingThresholds: [],
            mergedThresholds: [],
          };
        }

        // Set API thresholds
        state.byPaymentType[paymentTypeId].accounts[accountId].apiThresholds =
          thresholds;
        state.byPaymentType[paymentTypeId].accounts[
          accountId
        ].accountDetails = accountDetails;

        // Merge with pending thresholds
        const pending =
          state.byPaymentType[paymentTypeId].accounts[accountId]
            .pendingThresholds;
        state.byPaymentType[paymentTypeId].accounts[
          accountId
        ].mergedThresholds = [...thresholds, ...pending];

        console.log(
          `. [GX_REDUX] Account ${accountId} - API: ${thresholds.length}, Pending: ${pending.length}, Merged: ${[...thresholds, ...pending].length}`
        );
      });

      console.log(". [GX_REDUX] Batch operation complete");
    },
  },
});

export const {
  setApiThresholds,
  addPendingThreshold,
  editThreshold, // . NEW: Export edit action
  updateAccountDetails,
  setSelectedAccounts,
  clearSelectedAccounts,
  clearPendingThresholds,
  clearPaymentTypeData,
  resetAuthMatrix,
  batchSetApiThresholds,
} = authMatrixSlice.actions;

export default authMatrixSlice.reducer;

// . Selectors
export const selectAccountAuthMatrix = (
  state: { authMatrix: AuthMatrixState },
  paymentTypeId: number,
  accountId: number
) => {
  console.log(
    "🔍 [GX_REDUX] Selecting auth matrix for:",
    `PaymentType: ${paymentTypeId}, Account: ${accountId}`
  );

  const data =
    state.authMatrix.byPaymentType[paymentTypeId]?.accounts[accountId];

  if (data) {
    console.log("📊 [GX_REDUX] Retrieved data:", {
      apiCount: data.apiThresholds.length,
      pendingCount: data.pendingThresholds.length,
      mergedCount: data.mergedThresholds.length,
    });
  } else {
    console.log("⚠️ [GX_REDUX] No data found");
  }

  return data;
};

export const selectMergedThresholds = (
  state: { authMatrix: AuthMatrixState },
  paymentTypeId: number,
  accountId: number
) => {
  console.log(
    "🔍 [GX_REDUX] Selecting merged thresholds for:",
    `PaymentType: ${paymentTypeId}, Account: ${accountId}`
  );

  const thresholds =
    state.authMatrix.byPaymentType[paymentTypeId]?.accounts[accountId]
      ?.mergedThresholds || [];

  console.log("📊 [GX_REDUX] Merged thresholds count:", thresholds.length);

  return thresholds;
};

export const selectAllAccountsForPaymentType = (
  state: { authMatrix: AuthMatrixState },
  paymentTypeId: number
) => {
  console.log(
    "🔍 [GX_REDUX] Selecting all accounts for PaymentType:",
    paymentTypeId
  );

  const accounts = state.byPaymentType[paymentTypeId]?.accounts || {};

  console.log("📊 [GX_REDUX] Total accounts:", Object.keys(accounts).length);

  return accounts;
};

// . Selector for selected accounts
export const selectSelectedAccounts = (
  state: { authMatrix: AuthMatrixState },
  paymentTypeId: number
) => {
  console.log(
    "🔍 [GX_REDUX] Selecting selected accounts for PaymentType:",
    paymentTypeId
  );

  const selectedIds = state.authMatrix.selectedAccounts[paymentTypeId] || [];

  console.log("📊 [GX_REDUX] Selected account IDs:", selectedIds);

  return selectedIds;
};